import { Star, ArrowRight } from 'lucide-react';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { useState } from 'react';
import { BookingModal } from './BookingModal';

const rooms = [
  {
    id: 1,
    type: 'Suite Room',
    count: 5,
    images: [
      '/images/rooms/Suite1.jpg',
      '/images/rooms/Suite2.jpg',
      '/images/rooms/Suite3.jpg'
    ],
    showImages: true,
    description: 'Spacious suites with premium furnishings, separate living area, and stunning city views',
    pricing: {
      single: '₹1,750',
      double: '₹2,500',
      extraBed: '₹500'
    },
    capacity: '1-2 Guests',
    size: '450 sq ft',
    amenities: ['AC', 'TV', 'Wi-Fi', 'Premium Bedding', 'Hot Water', 'Work Desk'],
    features: ['City View', 'Separate Living Area', 'Premium Bathroom'],
    rating: 4.8,
    reviews: 24,
  },
  {
    id: 2,
    type: 'Royal Room',
    count: 14,
    images: [
      '/images/rooms/royal.jpg'
    ],
    showImages: true,
    description: 'Comfortable royal rooms perfect for couples or business travelers with modern amenities',
    pricing: {
      single: '₹1,500',
      double: '₹2,000',
      extraBed: '₹500'
    },
    capacity: '1-2 Guests',
    size: '320 sq ft',
    amenities: ['AC', 'TV', 'Wi-Fi', 'Hot Water', 'Work Desk', 'Safe'],
    features: ['City View', 'Modern Bathroom', 'Comfortable Seating'],
    rating: 4.6,
    reviews: 18,
  },
  {
    id: 3,
    type: 'Single Room',
    count: 8,
    images: [
      '/images/rooms/Single.jpg',
      '/images/rooms/Single2.jpg'
    ],
    showImages: true,
    description: 'Cozy single rooms ideal for solo travelers with all essential amenities and comfort',
    pricing: {
      single: '₹900',
      double: '₹1,200',
      extraBed: '₹400'
    },
    capacity: '1-2 Guests',
    size: '220 sq ft',
    amenities: ['AC', 'TV', 'Wi-Fi', 'Work Desk', 'Reading Light', 'Storage'],
    features: ['Compact Design', 'Efficient Layout', 'Solo Traveler Friendly'],
    rating: 4.4,
    reviews: 32,
  },
];

export function RoomGallery() {
  const [currentImageIndex, setCurrentImageIndex] = useState<{[key: number]: number}>({});
  const [currentRoomIndex, setCurrentRoomIndex] = useState(0);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [selectedRoomType, setSelectedRoomType] = useState<'Single' | 'Royal' | 'Suite'>('Royal');

  const handleCheckAvailability = (roomType: string) => {
    const message = `Hi, I'd like to check availability for ${roomType} at Hotel A.S.K Grand`;
    const whatsappUrl = `https://wa.me/917799933325?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleBookWithPayment = (roomType: 'Single' | 'Royal' | 'Suite') => {
    setSelectedRoomType(roomType);
    setIsBookingModalOpen(true);
  };

  const handleImageClick = (roomId: number, imageIndex: number) => {
    setCurrentImageIndex(prev => ({ ...prev, [roomId]: imageIndex }));
  };

  const handlePreviousRoom = () => {
    setCurrentRoomIndex((prev) => (prev === 0 ? rooms.length - 1 : prev - 1));
  };

  const handleNextRoom = () => {
    setCurrentRoomIndex((prev) => (prev === rooms.length - 1 ? 0 : prev + 1));
  };

  const currentRoom = rooms[currentRoomIndex];

  return (
    <section id="rooms" className="py-16 sm:py-24 bg-luxury-gradient relative overflow-hidden">
      {/* Luxury Background Pattern */}
      <div className="absolute inset-0 bg-luxury-pattern opacity-30"></div>
      
      <div className="relative w-full px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="text-center mb-12">
          <div className="inline-block glass-luxury border border-gold-400/30 text-gold-300 px-6 py-3 rounded-full text-sm font-medium mb-6">
            Premium Luxury Suites Available
          </div>
          <h2 className="mb-6 text-white">Choose Your Perfect Suite</h2>
          <p className="text-cream-200 max-w-3xl mx-auto text-lg">
            Experience unparalleled comfort and luxury in our thoughtfully designed suites. Each space is crafted to provide you with the perfect blend of modern amenities and sophisticated atmosphere.
          </p>
        </div>

        {/* Single Room Display with Navigation */}
        <div className="relative mb-16 max-w-6xl mx-auto px-4 sm:px-8 lg:px-16">
          {/* Room Card */}
          <div className="glass-luxury rounded-3xl shadow-luxury-xl overflow-hidden border border-gold-400/20">
            <div className="grid lg:grid-cols-2 gap-0">
              {/* Room Details */}
              <div className="p-6 lg:p-8 bg-gradient-to-b from-navy-900/95 to-navy-800/95 backdrop-blur-sm flex flex-col">
                {/* Header */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-2xl lg:text-3xl font-bold text-gold-400">{currentRoom.type}</h3>
                    <div className="flex items-center gap-1 bg-gold-400/20 px-3 py-1.5 rounded-full">
                      <Star className="w-4 h-4 text-gold-400 fill-current" />
                      <span className="text-sm font-bold text-gold-400">{currentRoom.rating}</span>
                    </div>
                  </div>
                  <p className="text-cream-300 text-sm">{currentRoom.size} • {currentRoom.capacity}</p>
                  <div className="mt-2 inline-block bg-gold-gradient text-navy-900 px-3 py-1 rounded-full text-xs font-bold">
                    Available
                  </div>
                </div>

                <p className="text-cream-200 mb-4 text-sm leading-relaxed">{currentRoom.description}</p>

                {/* Features */}
                <div className="mb-4">
                  <h4 className="text-sm font-bold text-gold-400 mb-2">Luxury Features</h4>
                  <div className="flex flex-wrap gap-1.5">
                    {currentRoom.features.map((feature, index) => (
                      <span
                        key={index}
                        className="bg-gold-400/20 text-gold-300 px-2.5 py-1 rounded-full text-xs font-semibold border border-gold-400/30"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Amenities */}
                <div className="mb-4">
                  <h4 className="text-sm font-bold text-gold-400 mb-2">Premium Amenities</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {currentRoom.amenities.map((amenity, index) => (
                      <div key={index} className="flex items-center gap-1.5">
                        <div className="w-1.5 h-1.5 bg-gold-400 rounded-full"></div>
                        <span className="text-xs text-cream-200 font-medium">{amenity}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Pricing */}
                <div className="border-t border-gold-400/20 pt-4 mt-auto">
                  <div className="mb-4">
                    <h4 className="text-sm font-bold text-gold-400 mb-2">Pricing per Day</h4>
                    <div className="space-y-2 bg-navy-800/50 rounded-lg p-3 border border-gold-400/20">
                      <div className="flex justify-between items-center">
                        <span className="text-cream-300 text-sm">1 Person</span>
                        <span className="text-gold-400 font-bold text-lg">{currentRoom.pricing.single}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-cream-300 text-sm">2 Persons</span>
                        <span className="text-gold-400 font-bold text-lg">{currentRoom.pricing.double}</span>
                      </div>
                      {currentRoom.pricing.extraBed && (
                        <div className="flex justify-between items-center pt-2 border-t border-gold-400/10">
                          <span className="text-cream-300 text-sm">Extra Bed</span>
                          <span className="text-gold-400 font-semibold text-base">{currentRoom.pricing.extraBed}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <button
                    onClick={() => handleBookWithPayment(currentRoom.type as 'Single' | 'Royal' | 'Suite')}
                    className="w-full bg-gold-gradient text-navy-900 py-3 rounded-xl hover:shadow-gold-xl transition-all duration-300 font-bold text-sm transform hover:scale-105 flex items-center justify-center gap-2 mb-2"
                  >
                    Book with Payment
                    <ArrowRight className="w-4 h-4" />
                  </button>
                  
                  <button
                    onClick={() => handleCheckAvailability(currentRoom.type)}
                    className="w-full bg-white/10 text-gold-400 border-2 border-gold-400/30 py-3 rounded-xl hover:bg-gold-400/10 transition-all duration-300 font-bold text-sm"
                  >
                    Check Availability
                  </button>
                </div>
              </div>

              {/* Photo Slideshow */}
              <div className="relative bg-navy-900 h-[400px] lg:h-auto flex items-center justify-center">
                <div className="relative w-full h-full">
                  <ImageWithFallback
                    src={currentRoom.images[currentImageIndex[currentRoom.id] || 0]}
                    alt={currentRoom.type}
                    className="w-full h-full object-cover"
                  />
                  
                  {/* Photo Navigation Arrows */}
                  {currentRoom.images.length > 1 && (
                    <>
                      <button
                        onClick={() => {
                          const currentIndex = currentImageIndex[currentRoom.id] || 0;
                          const newIndex = currentIndex === 0 ? currentRoom.images.length - 1 : currentIndex - 1;
                          handleImageClick(currentRoom.id, newIndex);
                        }}
                        className="absolute left-3 top-1/2 -translate-y-1/2 bg-gold-gradient text-navy-900 p-2 rounded-full shadow-gold-xl hover:scale-110 transition-transform z-10"
                        aria-label="Previous image"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      
                      <button
                        onClick={() => {
                          const currentIndex = currentImageIndex[currentRoom.id] || 0;
                          const newIndex = (currentIndex + 1) % currentRoom.images.length;
                          handleImageClick(currentRoom.id, newIndex);
                        }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 bg-gold-gradient text-navy-900 p-2 rounded-full shadow-gold-xl hover:scale-110 transition-transform z-10"
                        aria-label="Next image"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </>
                  )}
                  
                  {/* Image Navigation Dots */}
                  {currentRoom.images.length > 1 && (
                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-1.5 z-10">
                      {currentRoom.images.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => handleImageClick(currentRoom.id, index)}
                          className={`transition-all duration-300 ${
                            (currentImageIndex[currentRoom.id] || 0) === index 
                              ? 'w-6 h-2 bg-gold-400 rounded-full' 
                              : 'w-2 h-2 bg-white/60 rounded-full hover:bg-white/80'
                          }`}
                          aria-label={`Go to image ${index + 1}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Room Navigation Arrows */}
          <button
            onClick={handlePreviousRoom}
            className="hidden lg:block absolute -left-12 xl:-left-16 top-1/2 -translate-y-1/2 bg-gold-gradient text-navy-900 p-3 lg:p-4 rounded-full shadow-gold-xl hover:scale-110 transition-transform z-20"
            aria-label="Previous room"
          >
            <svg className="w-5 h-5 lg:w-6 lg:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          
          <button
            onClick={handleNextRoom}
            className="hidden lg:block absolute -right-12 xl:-right-16 top-1/2 -translate-y-1/2 bg-gold-gradient text-navy-900 p-3 lg:p-4 rounded-full shadow-gold-xl hover:scale-110 transition-transform z-20"
            aria-label="Next room"
          >
            <svg className="w-5 h-5 lg:w-6 lg:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {/* Room Navigation Dots */}
          <div className="flex justify-center gap-3 mt-8">
            {rooms.map((room, index) => (
              <button
                key={room.id}
                onClick={() => setCurrentRoomIndex(index)}
                className={`transition-all duration-300 ${
                  currentRoomIndex === index 
                    ? 'w-8 h-3 bg-gold-400 rounded-full' 
                    : 'w-3 h-3 bg-gold-400/30 rounded-full hover:bg-gold-400/50'
                }`}
                aria-label={`View ${room.type}`}
              />
            ))}
          </div>
          
          {/* Room Type Indicator */}
          <div className="text-center mt-4">
            <p className="text-cream-300 text-sm">
              {currentRoom.type} ({currentRoomIndex + 1} of {rooms.length})
            </p>
          </div>
        </div>

        {/* Special Offers Section */}
        <div className="bg-gold-gradient rounded-3xl p-10 text-navy-900 text-center mb-12 shadow-gold-xl">
          <h3 className="text-3xl font-bold mb-6">Exclusive Grand Packages</h3>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="glass-navy rounded-2xl p-8 border border-navy-400/30">
              <h4 className="font-bold text-lg mb-3 text-navy-900">Early Bird Luxury</h4>
              <p className="text-sm text-navy-700 mb-4">Book 7 days in advance and save up to 25% on luxury suites</p>
              <span className="text-navy-900 font-bold text-lg">Save ₹500</span>
            </div>
            <div className="glass-navy rounded-2xl p-8 border border-navy-400/30">
              <h4 className="font-bold text-lg mb-3 text-navy-900">Suite Room Special</h4>
              <p className="text-sm text-navy-700 mb-4">Free breakfast for all suite room bookings</p>
              <span className="text-navy-900 font-bold text-lg">Free Breakfast</span>
            </div>
            <div className="glass-navy rounded-2xl p-8 border border-navy-400/30">
              <h4 className="font-bold text-lg mb-3 text-navy-900">Family Special</h4>
              <p className="text-sm text-navy-700 mb-4">Children up to 10 years stay free with parents</p>
              <span className="text-navy-900 font-bold text-lg">Kids Stay Free</span>
            </div>
          </div>
        </div>

        {/* Help Section */}
        <div className="glass-luxury rounded-3xl shadow-luxury-xl p-10 text-center border border-gold-400/20">
          <h3 className="text-3xl font-bold text-gold-400 mb-6">Need Personalized Assistance?</h3>
          <p className="text-cream-200 mb-8 max-w-2xl mx-auto text-lg">
            Our luxury concierge team is ready to help you find the perfect suite for your stay. Contact us for personalized recommendations and exclusive VIP rates.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <a
              href="tel:+917799933325"
              className="inline-flex items-center justify-center gap-3 bg-gold-gradient text-navy-900 px-10 py-4 rounded-xl hover:shadow-gold-xl transition-all duration-300 font-bold text-lg transform hover:scale-105"
            >
              Call Luxury Concierge
              <ArrowRight className="w-5 h-5" />
            </a>
            <button
              onClick={() => handleCheckAvailability('luxury suite consultation')}
              className="inline-flex items-center justify-center gap-3 border-2 border-gold-400 text-gold-400 px-10 py-4 rounded-xl hover:bg-gold-400/10 hover:border-gold-300 transition-all duration-300 font-bold text-lg transform hover:scale-105"
            >
              Chat on WhatsApp
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Booking Modal */}
      <BookingModal
        isOpen={isBookingModalOpen}
        onClose={() => setIsBookingModalOpen(false)}
        preselectedRoomType={selectedRoomType}
      />
    </section>
  );
}
